// Shared API infrastructure — axios instance, auth helpers, caching, interceptors
import axios from 'axios';
import { config } from '../config/environment';
import { apiCircuitBreaker, presignedUrlCircuitBreaker } from '../utils/circuitBreaker';
import { generateUUID } from '../utils/uuid';

export const S3_BASE_URL = config.assets.s3BaseUrl;

// Debug-gated logger — suppresses verbose output in production builds.
export const dbg = {
  log: (...args) => { if (config.features.enableDebugLogs) console.log(...args); },
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

// Cache for presigned URLs (expires after 50 minutes — URLs expire in 60)
export const presignedUrlCache = new Map();
export const PRESIGNED_URL_CACHE_TTL = 50 * 60 * 1000;

// Get auth headers with Clerk token
export const getAuthHeaders = async (userInfo = null) => {
  const headers = {};
  try {
    if (window.Clerk && window.Clerk.session) {
      const token = await window.Clerk.session.getToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const userId = window.Clerk.session.userId || window.Clerk.user?.id;
      if (userId) headers['x-user-id'] = userId;
    }
    if (userInfo?.user?.id && !headers['x-user-id']) {
      headers['x-user-id'] = userInfo.user.id;
    }
  } catch (error) {
    console.warn('Could not get Clerk token:', error);
  }
  return headers;
};

// Axios instance
const api = axios.create({
  baseURL: config.api.baseUrl,
  timeout: config.api.timeout,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor — attach correlation ID and debug logging
api.interceptors.request.use(
  (reqCfg) => {
    reqCfg.headers['X-Request-Id'] =
      reqCfg.headers['X-Request-Id'] || generateUUID();
    if (process.env.NODE_ENV === 'development') {
      dbg.log('API Request:', {
        method: reqCfg.method,
        url: reqCfg.url,
        baseURL: reqCfg.baseURL,
        requestId: reqCfg.headers['X-Request-Id']
      });
    }
    return reqCfg;
  },
  (error) => Promise.reject(error)
);

// Retry with exponential backoff for transient failures (5xx, 429, network)
const MAX_RETRIES = config.api.retries || 3;
const RETRY_BASE_DELAY = config.performance?.apiRetryDelay || 1000;

function isRetryable(error) {
  if (!error) return false;
  const status = error.response?.status;
  if (status === 429 || (status >= 500 && status <= 504)) return true;
  if (!error.response && (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED' || error.code === 'ECONNABORTED')) return true;
  return false;
}

api.interceptors.response.use(null, async (error) => {
  const cfg = error.config;
  if (!cfg) return Promise.reject(error);
  cfg.__retryCount = cfg.__retryCount || 0;
  if (isRetryable(error) && cfg.__retryCount < MAX_RETRIES) {
    cfg.__retryCount += 1;
    let delay;
    if (error.response?.status === 429) {
      const retryAfter = error.response.headers?.['retry-after'];
      delay = retryAfter ? Number(retryAfter) * 1000 : RETRY_BASE_DELAY * Math.pow(2, cfg.__retryCount - 1);
    } else {
      delay = RETRY_BASE_DELAY * Math.pow(2, cfg.__retryCount - 1);
    }
    const jitter = Math.random() * 1000;
    delay += jitter;
    dbg.log(`Retry ${cfg.__retryCount}/${MAX_RETRIES} for ${cfg.method?.toUpperCase()} ${cfg.url} in ${delay.toFixed(0)}ms (+${jitter.toFixed(0)}ms jitter)`);
    await new Promise(r => setTimeout(r, delay));
    return api(cfg);
  }
  return Promise.reject(error);
});

// Response interceptor — circuit breaker + error normalization
api.interceptors.response.use(
  (response) => {
    apiCircuitBreaker.onSuccess();
    return response;
  },
  (error) => {
    const requestUrl = error.config?.url || '';
    const status = error.response?.status;
    const requestId = error.config?.headers?.['X-Request-Id'] || 'unknown';
    const isPresignedUrlRequest = requestUrl.includes('/presigned-url');
    const isCorsOrForbidden = status === 403 || (!error.response && error.message?.includes('Network Error'));

    if (status !== 404 && status !== 401 && !isPresignedUrlRequest && !isCorsOrForbidden) {
      apiCircuitBreaker.onFailure();
    }

    const breakerState = apiCircuitBreaker.getState();
    if (breakerState.state === 'OPEN') {
      const timeUntilReset = Math.ceil((breakerState.nextAttempt - Date.now()) / 1000);
      error.message = `Service temporarily unavailable. Please try again in ${timeUntilReset} seconds.`;
      error.code = 'CIRCUIT_BREAKER_OPEN';
    }

    if (status !== 404) {
      console.error('API Error:', {
        requestId,
        url: error.config?.url,
        method: error.config?.method,
        status,
        data: error.response?.data,
        circuitBreaker: breakerState.state
      });
    }

    if (status === 401) {
      try {
        // If the user is already signed in, the token is likely just stale
        // after an OAuth redirect. Redirecting to sign-in here would create
        // an infinite loop (Clerk sees them signed-in and bounces back).
        if (window.Clerk?.session) {
          console.warn('401 received while signed in — token may be stale, skipping redirect');
        } else if (window.Clerk?.redirectToSignIn && !window.__CLERK_REDIRECT_IN_PROGRESS__) {
          window.__CLERK_REDIRECT_IN_PROGRESS__ = true;
          window.Clerk.redirectToSignIn();
        }
      } catch (_) {}
    }

    if (status === 403) {
      console.error('403 Forbidden — possible CORS or auth issue:', {
        requestId, url: error.config?.url, method: error.config?.method
      });
    }

    if (!error.response && (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED')) {
      error.message = 'Unable to connect to server. Please check your internet connection.';
    }
    if (error.code === 'ECONNABORTED') {
      error.message = 'Request timed out. Please try again.';
    }
    if (status === 429) {
      error.message = 'Too many requests. Please wait a moment and try again.';
    }

    return Promise.reject(error);
  }
);

export { presignedUrlCircuitBreaker };
export default api;
